import React, {
  memo,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import moment from 'moment'
import { Id, TimelineGroupBase } from 'react-calendar-timeline'
import { TimeOff } from '../../types/TimeOff'

const MONTHS_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

const CELL_WIDTH = 30
const CELL_HEIGHT = 40
const NAME_COL_WIDTH = 220
const WINDOW_SIZE = 31
const EMPTY_TIME_OFF_MAP = new Map<number, TimeOff>()

const buildDayRange = () => {
  const todayM = moment().startOf('day')
  // Always show previous year, current year, and next year
  const rangeStart = todayM.clone().subtract(1, 'year').startOf('year')
  const rangeEnd = todayM.clone().add(1, 'year').endOf('year')

  const totalDays = rangeEnd.diff(rangeStart, 'days') + 1
  const days = Array.from({ length: totalDays }, (_, index) =>
    rangeStart.clone().add(index, 'days').diff(todayM, 'days'),
  )

  return { days, firstOffset: days[0] ?? 0 }
}

const fmtDate = (d: Date) =>
  `${MONTHS_SHORT[d.getMonth()]} ${String(d.getDate()).padStart(
    2,
    '0',
  )}, ${d.getFullYear()}`

const addDays = (base: Date, n: number) => {
  const d = new Date(base)
  d.setDate(d.getDate() + n)
  return d
}

interface DayMeta {
  offset: number
  dayOfMonth: string
  isToday: boolean
  isWeekend: boolean
  isMonthStart: boolean
}

interface CalendarRowProps {
  group: TimelineGroupBase
  daysMeta: DayMeta[]
  timeOffByOffset: Map<number, TimeOff>
  canInteract: boolean
  groupNamesClickable: boolean
  onGroupNameClick?: (groupId: Id) => void
  onItemSelect?: (itemId: Id) => void
}

const CalendarRow = memo(function CalendarRow({
  group,
  daysMeta,
  timeOffByOffset,
  canInteract,
  groupNamesClickable,
  onGroupNameClick,
  onItemSelect,
}: CalendarRowProps) {
  return (
    <tr className="team-timeoff__row">
      <td
        className={[
          'team-timeoff__member-cell',
          groupNamesClickable ? 'team-timeoff__member-cell--clickable' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        style={{ width: NAME_COL_WIDTH, height: CELL_HEIGHT }}
        onClick={() => {
          if (groupNamesClickable && onGroupNameClick) {
            onGroupNameClick(group.id)
          }
        }}
      >
        {group.title}
      </td>
      {daysMeta.map(day => {
        const off = timeOffByOffset.get(day.offset)
        const showTimeOff = Boolean(off) && !day.isWeekend
        const cellClass = [
          'team-timeoff__day-cell',
          showTimeOff ? 'team-timeoff__day-cell--timeoff' : '',
          day.isWeekend ? 'team-timeoff__day-cell--weekend' : '',
          !showTimeOff && !day.isWeekend && day.isToday
            ? 'team-timeoff__day-cell--today'
            : '',
          day.isToday ? 'team-timeoff__day-cell--today-col' : '',
          day.isMonthStart ? 'team-timeoff__day-cell--month-start' : '',
          canInteract && showTimeOff ? 'team-timeoff__day-cell--clickable' : '',
        ]
          .filter(Boolean)
          .join(' ')

        return (
          <td
            key={day.offset}
            className={cellClass}
            style={{ width: CELL_WIDTH, height: CELL_HEIGHT }}
            title={
              canInteract && showTimeOff
                ? off!.description || off!.name
                : undefined
            }
            onClick={() => {
              if (
                canInteract &&
                showTimeOff &&
                onItemSelect &&
                off!.id != null
              ) {
                onItemSelect(off!.id)
              }
            }}
          />
        )
      })}
    </tr>
  )
})

interface TeamTimeOffCalendarProps {
  usersGroups: TimelineGroupBase[]
  allTimesOff: TimeOff[]
  readOnly?: boolean
  isGroupNameClickable?: boolean
  onGroupNameClick?: (groupId: Id) => void
  onItemSelect?: (itemId: Id) => void
}

const TeamTimeOffCalendar: React.FC<TeamTimeOffCalendarProps> = ({
  usersGroups,
  allTimesOff,
  readOnly = false,
  isGroupNameClickable,
  onGroupNameClick,
  onItemSelect,
}) => {
  const canInteract = !readOnly
  const groupNamesClickable = canInteract && isGroupNameClickable

  const today = useMemo(() => {
    const t = new Date()
    t.setHours(0, 0, 0, 0)
    return t
  }, [])

  const todayMoment = useMemo(() => moment().startOf('day'), [])

  const { days, firstOffset } = useMemo(() => buildDayRange(), [])

  const daysMeta = useMemo((): DayMeta[] => {
    return days.map(offset => {
      const d = addDays(today, offset)
      return {
        offset,
        dayOfMonth: String(d.getDate()).padStart(2, '0'),
        isToday: offset === 0,
        isWeekend: d.getDay() === 0 || d.getDay() === 6,
        isMonthStart: d.getDate() === 1 && offset !== firstOffset,
      }
    })
  }, [days, today, firstOffset])

  const timeOffByUser = useMemo(() => {
    if (!days.length) return new Map<number, Map<number, TimeOff>>()

    const byUser = new Map<number, Map<number, TimeOff>>()
    const minOffset = days[0]
    const maxOffset = days[days.length - 1]
    const todayM = todayMoment

    allTimesOff.forEach(timeOff => {
      const start = moment(timeOff.startDate).startOf('day')
      const end = moment(timeOff.endDate).startOf('day')
      const startOffset = start.diff(todayM, 'days')
      const endOffset = end.diff(todayM, 'days')

      if (endOffset < minOffset || startOffset > maxOffset) return

      if (!byUser.has(timeOff.userId)) {
        byUser.set(timeOff.userId, new Map())
      }
      const userMap = byUser.get(timeOff.userId)!

      const from = Math.max(startOffset, minOffset)
      const to = Math.min(endOffset, maxOffset)
      for (let offset = from; offset <= to; offset++) {
        if (!userMap.has(offset)) {
          userMap.set(offset, timeOff)
        }
      }
    })

    return byUser
  }, [allTimesOff, days, todayMoment])

  const scrollRef = useRef<HTMLDivElement>(null)
  const memberHeaderRef = useRef<HTMLTableCellElement>(null)
  const todayHeaderRef = useRef<HTMLTableCellElement>(null)
  const scrollRafRef = useRef<number | null>(null)
  const firstVisibleOffsetRef = useRef(0)
  const prevFirstOffsetRef = useRef<number | undefined>(undefined)
  const [firstVisibleOffset, setFirstVisibleOffset] = useState(0)

  const updateVisibleRange = useCallback(() => {
    const container = scrollRef.current
    const memberHeader = memberHeaderRef.current
    if (!container || !memberHeader) return

    const memberRight = memberHeader.getBoundingClientRect().right
    const dayHeaders =
      container.querySelectorAll<HTMLElement>('[data-day-offset]')

    for (let i = 0; i < dayHeaders.length; i++) {
      const header = dayHeaders[i]
      if (header.getBoundingClientRect().right <= memberRight) continue
      const nextOffset = Number(header.dataset.dayOffset)
      if (nextOffset !== firstVisibleOffsetRef.current) {
        firstVisibleOffsetRef.current = nextOffset
        setFirstVisibleOffset(nextOffset)
      }
      return
    }
  }, [])

  const scrollToToday = useCallback((): boolean => {
    const container = scrollRef.current
    const todayHeader = todayHeaderRef.current
    const memberHeader = memberHeaderRef.current
    if (!container || !todayHeader || !memberHeader) return false

    const containerRect = container.getBoundingClientRect()
    const targetLeft =
      memberHeader.getBoundingClientRect().right - containerRect.left
    const currentLeft =
      todayHeader.getBoundingClientRect().left - containerRect.left

    container.scrollLeft += currentLeft - targetLeft
    updateVisibleRange()
    return true
  }, [updateVisibleRange])

  const handleScroll = useCallback(() => {
    if (scrollRafRef.current != null) return

    scrollRafRef.current = requestAnimationFrame(() => {
      scrollRafRef.current = null
      updateVisibleRange()
    })
  }, [updateVisibleRange])

  useLayoutEffect(() => {
    if (!days.length) return

    const rangeStartChanged =
      prevFirstOffsetRef.current !== undefined &&
      prevFirstOffsetRef.current !== firstOffset

    if (rangeStartChanged) {
      const container = scrollRef.current
      if (container) {
        const offsetDelta =
          (prevFirstOffsetRef.current! - firstOffset) * CELL_WIDTH
        container.scrollLeft += offsetDelta
        updateVisibleRange()
      }
    }

    const shouldScrollToToday =
      prevFirstOffsetRef.current === undefined || rangeStartChanged

    if (shouldScrollToToday) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          scrollToToday()
        })
      })
    }

    prevFirstOffsetRef.current = firstOffset
  }, [
    firstOffset,
    days.length,
    scrollToToday,
    updateVisibleRange,
    usersGroups.length,
  ])

  const jumpToToday = () => {
    scrollToToday()
  }

  const viewportWidth =
    (scrollRef.current?.clientWidth ??
      NAME_COL_WIDTH + WINDOW_SIZE * CELL_WIDTH) - NAME_COL_WIDTH
  const visibleCount = Math.max(1, Math.floor(viewportWidth / CELL_WIDTH))
  const lastVisibleOffset = firstVisibleOffset + visibleCount - 1
  const visibleStart = addDays(today, firstVisibleOffset)
  const visibleEnd = addDays(today, lastVisibleOffset)

  const monthGroups = useMemo(() => {
    const groups: { label: string; count: number; startOffset: number }[] = []
    daysMeta.forEach(day => {
      const d = addDays(today, day.offset)
      const label = `${MONTHS_SHORT[d.getMonth()]} ${d.getFullYear()}`
      if (groups.length === 0 || groups[groups.length - 1].label !== label) {
        groups.push({ label, count: 1, startOffset: day.offset })
      } else {
        groups[groups.length - 1].count++
      }
    })
    return groups
  }, [daysMeta, today])

  return (
    <>
      <div className="team-timeoff__calendar-nav">
        <div className="team-timeoff__calendar-nav-left">
          <span className="team-timeoff__date-range">
            {fmtDate(visibleStart)} → {fmtDate(visibleEnd)}
          </span>
          <button
            type="button"
            className="team-timeoff__jump-today"
            onClick={jumpToToday}
          >
            Jump to Today
          </button>
        </div>
        <div className="team-timeoff__legend">
          <div className="team-timeoff__legend-item">
            <span className="team-timeoff__legend-swatch team-timeoff__legend-swatch--available" />
            <span className="team-timeoff__legend-label">Available</span>
          </div>
          <div className="team-timeoff__legend-item">
            <span className="team-timeoff__legend-swatch team-timeoff__legend-swatch--weekend" />
            <span className="team-timeoff__legend-label">Weekend</span>
          </div>
          <div className="team-timeoff__legend-item">
            <span className="team-timeoff__legend-swatch team-timeoff__legend-swatch--timeoff" />
            <span className="team-timeoff__legend-label">Time Off</span>
          </div>
        </div>
      </div>

      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="team-timeoff__calendar-scroll"
      >
        <table className="team-timeoff__table">
          <colgroup>
            <col style={{ width: NAME_COL_WIDTH }} />
            {days.map(offset => (
              <col key={offset} style={{ width: CELL_WIDTH }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              <th
                rowSpan={2}
                ref={memberHeaderRef}
                className="team-timeoff__members-header"
                style={{ width: NAME_COL_WIDTH }}
              >
                Members ({usersGroups.length})
              </th>
              {monthGroups.map(group => (
                <th
                  key={group.startOffset}
                  colSpan={group.count}
                  className="team-timeoff__month-header"
                >
                  {group.label}
                </th>
              ))}
            </tr>
            <tr>
              {daysMeta.map(day => (
                <th
                  key={day.offset}
                  ref={day.isToday ? todayHeaderRef : undefined}
                  data-day-offset={day.offset}
                  className={[
                    'team-timeoff__day-header',
                    day.isToday ? 'team-timeoff__day-header--today' : '',
                    day.isMonthStart
                      ? 'team-timeoff__day-header--month-start'
                      : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  style={{ width: CELL_WIDTH, height: CELL_HEIGHT }}
                >
                  <span className="team-timeoff__day-number">
                    {day.dayOfMonth}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {usersGroups.map(group => (
              <CalendarRow
                key={group.id}
                group={group}
                daysMeta={daysMeta}
                timeOffByOffset={
                  timeOffByUser.get(group.id as number) ?? EMPTY_TIME_OFF_MAP
                }
                canInteract={canInteract}
                groupNamesClickable={Boolean(groupNamesClickable)}
                onGroupNameClick={onGroupNameClick}
                onItemSelect={onItemSelect}
              />
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}

export default memo(TeamTimeOffCalendar)
